import { FC } from 'react';
import { IonAvatar } from '@ionic/react';
import { useResolvedMediaUrl } from '../media/useResolvedMediaUrl';
import { IFamilyMember } from '../caremap/ICareMap';
import { EditIcon2 } from '../theme/Icons';
import { HOUSEHOLD_MEMBER_AVATARS } from '../caremap/CareMapFactory';

export const MemberAvatar: FC<{
    // The photo link lives on IPatient and IHouseholdMember rather than the shared base, so it is
    // widened here instead: this renders both, and a member with no photo carries no link.
    member: Partial<IFamilyMember> & { links?: { photo?: { href: string } } };
    small?: boolean;
    xsmall?: boolean;
    // explicit pixel diameter; overrides the small/xsmall presets when set
    size?: number;
    onClick?: (e: React.MouseEvent) => void;
}> = ({ member, small = false, xsmall = false, size: sizeOverride, onClick }) => {
    // Only an uploaded photo is resolved. A chosen avatar and the default ship with the app, so
    // pointing those at the API would be wrong.
    //
    // The same stand-in serves both a member who never had a photo and one whose photo cannot be
    // shown right now, so a photo that fails to resolve lands on the ordinary no-photo appearance
    // rather than on an empty circle. Only a member with a colour is meant to show no image at all.
    const withoutPhoto = member.avatar || (!member.color ? HOUSEHOLD_MEMBER_AVATARS[0] : undefined);
    const resolvedPhoto = useResolvedMediaUrl({
        url: member.photo,
        href: member.links?.photo?.href,
        fallbackWhenUnavailable: withoutPhoto
    });
    const imageUrl = member.photo ? resolvedPhoto : withoutPhoto;
    const dim = sizeOverride ?? (xsmall ? 32 : small ? 42 : 64);
    const size = {width: dim, height: dim };

    const avatar = (
        <IonAvatar style={{ ...size, backgroundColor: member.color }} data-avatar={imageUrl || member.color}>
            {imageUrl &&
                <div style={{
                    width: '100%',
                    height: '100%',
                    borderRadius: '50%',
                    backgroundImage: `url("${imageUrl}")`,
                    // TODO: better way to set these up / scale / position them, based on our "profile image" effort in the future
                    position: 'relative',
                    backgroundSize: 'cover',
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: '75% 40%'
                }}></div>
            }
        </IonAvatar>
    );

    if (onClick) {
        return (
            <div style={{ cursor: 'pointer', position: 'relative'}} onClick={(e: React.MouseEvent) => onClick(e)}>
                {avatar}
                <div style={{ position: 'absolute', bottom: -2, right: -2 }}>
                    <EditIcon2 size="small" shape="round" backgroundColor="white" borderColor="medium" />
                </div>
            </div>
        );
    }
    else {
        return avatar;
    }
};
